import React, { useCallback, useMemo } from 'react';

import {
  Box,
  Checkbox,
  FormikFormControl,
  FormikInput,
  HStack,
} from '@rbilabs/universal-components';
import { Formik } from 'formik';
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import ActionButton from 'components/action-button';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { UserJourneyFormLabel } from 'components/quests/user-jouney-form-label';
import { IsoCountryCode, LoyaltyCmsType } from 'generated/graphql-gateway';
import { useLoyaltyContent } from 'hooks/loyalty/use-loyalty-content';
import { useFieldIsRequired } from 'hooks/use-field-is-required';
import {
  UserJourneyFields,
  UserJourneySections,
  getUserJourneyStepsForSection,
} from 'pages/loyalty/loyalty-user-journey/utils';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import { t, tRich } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { enableNumericPhoneValidation } from 'utils/environment';
import {
  POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES,
  formatDateObject as dobObjToString,
  dobStringToObj,
  sanitizePhoneNumber,
} from 'utils/form';
import { postalCodeInputProperties } from 'utils/form/constants';
import { IDateOfBirth } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import { routes } from 'utils/routing';

import { FormikBirthdayInput } from '../formik/birthday-input';
import CompletePhoneFromBrazeId from '../phone-from-braze-id/complete-phone-from-braze-id';

import { getFormErrors } from './get-form-errors';
import { TermsLink } from './styled';
import { TermsSection } from './terms-section';
import { IUserFormData, IUserInfoSubmitData } from './types';

const InputContainer = Box.withConfig({
  marginBottom: '$3',
});

interface IUserInfoFormProps {
  readOnly?: boolean;
  user: UserDetails | null;
  /**
   * TODO: this onSubmit requires that a promise be passed
   * because there's a `.then` chain after it's called.
   *
   * We should either move the after submit functionality up to the account-info page
   * or move the submit logic into this form.
   */
  onSubmit: (data: IUserInfoSubmitData) => Promise<any>;
  isLoading?: boolean;
}

export const UserInfoForm = ({
  onSubmit,
  readOnly = false,
  user,
  isLoading,
}: IUserInfoFormProps) => {
  const enableAdsPersonalizationOptOut = useFlag(
    LaunchDarklyFlag.ENABLE_ADS_PERSONALIZATION_OPT_OUT
  );

  const { region } = useLocale();
  const { birthdayLegalText } = useLoyaltyContent();
  const { loyaltyUserId } = useLoyaltyUserId();
  const { quest: challenge } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId || '',
    cmsType: LoyaltyCmsType.USER_JOURNEY,
  });

  const DOB_DEFAULT_OR_EMPTY = useMemo(
    () => POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES.some(v => v === user?.details?.dob || ''),
    [user]
  );

  const initialValues: IUserFormData = useMemo(() => {
    // dob will come in as string so let's convert it
    const dob = dobStringToObj(user?.details?.dob);
    return {
      agreesToTermsOfService: true,
      dob,
      email: user?.details?.email || '',
      name: user?.details?.name || '',
      phoneNumber: user?.details?.phoneNumber || '',
      promotionalEmails: !!user?.details?.promotionalEmails || false,
      isoCountryCode: (user?.details?.isoCountryCode as IsoCountryCode) || IsoCountryCode.USA,
      zipcode: user?.details?.zipcode || '',
      defaultReloadAmt: user?.details?.defaultReloadAmt || 500,
      defaultReloadPaymentMethodId: user?.details?.defaultPaymentAccountId || '',
      defaultCheckoutPaymentMethodId:
        user?.details?.defaultAccountIdentifier || user?.details?.defaultFdAccountId || '',
      autoReloadEnabled: user?.details?.autoReloadEnabled || false,
      autoReloadThreshold: user?.details?.autoReloadThreshold || 500,
      adsPersonalizationOptOut: user?.details?.adsPersonalizationOptOut ?? false,
    };
  }, [user]);

  const handleSubmit = useCallback(
    async (formData: IUserFormData) => {
      const dob = dobObjToString(formData.dob as IDateOfBirth);

      const phoneNumber = formData.phoneNumber
        ? await sanitizePhoneNumber(formData.phoneNumber, formData.isoCountryCode)
        : null;
      await onSubmit({
        ...formData,
        phoneNumber,
        dob,
      });
    },
    [onSubmit]
  );

  const sendPersonalizationOptOutClickEvent = (checked: boolean) => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        name: 'internet-based advertising checkbox',
        checked,
      },
    });
  };

  const birthdayIsRequired = useFieldIsRequired(region, validationConfig, 'dob');
  const zipcodeIsRequired = useFieldIsRequired(region, validationConfig, 'zipcode');
  const phoneNumberIsRequired = useFieldIsRequired(region, validationConfig, 'phoneNumber');

  const challengeSteps =
    (challenge && getUserJourneyStepsForSection(challenge, UserJourneySections.ProfileSetting)) ||
    {};

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={getFormErrors({
        region,
        simplePhoneValidation: enableNumericPhoneValidation(),
      })}
      enableReinitialize
    >
      {({
        handleBlur,
        submitForm,
        values,
        setFieldValue,
        isValid,
        isSubmitting,
        setFieldTouched,
        errors,
        touched,
      }) => {
        const dobLabel = t('dateOfBirth');
        const zipCodeLabel =
          values.isoCountryCode === IsoCountryCode.USA ? t('zipCode') : t('postalCode');

        return (
          <>
            <CompletePhoneFromBrazeId setFieldValue={setFieldValue} />
            <InputContainer>
              <FormikFormControl name="email" label={t('yourEmail')} isReadOnly testID="email">
                <FormikInput
                  name="email"
                  inputProps={{
                    isDisabled: true,
                    isRequired: true,
                    testID: 'account-info-email-input',
                  }}
                />
              </FormikFormControl>
              <FormikFormControl name="name" label={t('name')} isRequired>
                <FormikInput
                  name="name"
                  inputProps={{
                    isRequired: true,
                    isDisabled: readOnly,
                    testID: 'account-info-name-input',
                    maxLength: 100,
                  }}
                />
              </FormikFormControl>
              <FormikFormControl
                isRequired={phoneNumberIsRequired}
                name="phoneNumber"
                label={t('phoneNumber')}
              >
                <FormikInput
                  name="phoneNumber"
                  inputProps={{
                    placeholder: `${t('phoneNumber')}`,
                    testID: 'signup-phone-input',
                    autoComplete: 'tel',
                    isDisabled: readOnly,
                    keyboardType: 'phone-pad',
                  }}
                />
              </FormikFormControl>
              <FormikFormControl
                name="dob"
                label={
                  challengeSteps[UserJourneyFields.DateOfBirth] ? (
                    <UserJourneyFormLabel
                      label={dobLabel}
                      step={challengeSteps[UserJourneyFields.DateOfBirth]}
                      fieldName={UserJourneyFields.DateOfBirth}
                      fieldType="string"
                    />
                  ) : (
                    dobLabel
                  )
                }
              >
                <FormikBirthdayInput
                  disabled={readOnly || !DOB_DEFAULT_OR_EMPTY}
                  hintMessage={birthdayLegalText ?? ''}
                  isRequired={birthdayIsRequired}
                  year={values.dob.year}
                  month={values.dob.month}
                  day={values.dob.day}
                  onChange={(inputName, inputText) => setFieldValue(`dob.${inputName}`, inputText)}
                  testID="account-info-dob"
                  onBlur={inputName => setFieldTouched(`dob.${inputName}`, true)}
                  dobErrors={errors.dob}
                  dobTouched={touched.dob}
                />
              </FormikFormControl>
              <FormikFormControl
                name="zipcode"
                isRequired={zipcodeIsRequired}
                label={
                  challengeSteps[UserJourneyFields.ZipCode] ? (
                    <UserJourneyFormLabel
                      label={zipCodeLabel}
                      step={challengeSteps[UserJourneyFields.ZipCode]}
                      fieldName={UserJourneyFields.ZipCode}
                      fieldType="string"
                    />
                  ) : (
                    zipCodeLabel
                  )
                }
              >
                <FormikInput
                  name="zipcode"
                  inputProps={{
                    placeholder:
                      values.isoCountryCode === IsoCountryCode.USA ? t('zipCode') : t('postalCode'),
                    isDisabled: readOnly,
                    testID: 'account-info-zip-input',
                    onBlur: () => handleBlur('zipcode'),
                    onChange: (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
                      setFieldValue('zipcode', event.target.toString().toUpperCase());
                    },
                    maxLength: postalCodeInputProperties[values.isoCountryCode]?.maxLength || 10,
                    autoComplete: 'postal-code',
                    ...(values.isoCountryCode === IsoCountryCode.USA && {
                      keyboardType: 'phone-pad',
                    }),
                  }}
                />
              </FormikFormControl>
              {enableAdsPersonalizationOptOut && (
                <>
                  <HStack alignItems="center" mt="$2">
                    <Checkbox
                      value={'ads-personalization-opt-out'}
                      accessibilityLabel={t('adsPersonalization')}
                      isChecked={!values.adsPersonalizationOptOut}
                      onChange={checked => {
                        sendPersonalizationOptOutClickEvent(checked);
                        setFieldValue('adsPersonalizationOptOut', !checked);
                      }}
                    />
                    <Typography ml="$3" style={{ flex: 1 }}>
                      {t('adsPersonalization')}
                    </Typography>
                  </HStack>
                  <Typography my="$2">
                    {tRich('adsPersonalizationInfo', {
                      privacyPolicyLink: (
                        <TermsLink to={routes.privacyPolicy}>{t('privacyPolicy')}</TermsLink>
                      ),
                    })}
                  </Typography>
                </>
              )}
            </InputContainer>
            <Box>
              <ActionButton
                testID="account-info-form-submit-button"
                fullWidth
                onPress={submitForm}
                disabled={readOnly || !isValid || isLoading || isSubmitting}
                isLoading={isLoading || isSubmitting}
              >
                {t('update')}
              </ActionButton>
            </Box>
            <TermsSection />
          </>
        );
      }}
    </Formik>
  );
};
